export const DEPLOY_JOB_POLL_MS = 2500;

function extractMessage(payload, fallback) {
	if (typeof payload === "string" && payload.trim()) return payload;
	if (payload && typeof payload.message === "string" && payload.message.trim()) {
		return payload.message;
	}
	if (payload?.message && typeof payload.message.message === "string") {
		return payload.message.message;
	}
	if (payload && typeof payload.detail === "string" && payload.detail.trim()) {
		return payload.detail;
	}
	return fallback;
}

export function parseDeployResponse(res, fallbackMessage = "Request failed") {
	if (res === "Invalid" || res === undefined || res === null) {
		return { ok: false, message: fallbackMessage, data: null };
	}
	if (res.status === "auth_error") {
		return { ok: false, message: extractMessage(res, fallbackMessage), data: null };
	}
	if (
		res.status === false ||
		res.status === "failure" ||
		res.status === "api_error" ||
		res.status === "server_error" ||
		res.status === "application_error" ||
		res.status === "not_found_error"
	) {
		return {
			ok: false,
			message: extractMessage(res.message ?? res, fallbackMessage),
			data: res.data ?? null,
		};
	}
	if (res.status !== "success" && res.status !== true) {
		return {
			ok: false,
			message: extractMessage(res.message ?? res, fallbackMessage),
			data: null,
		};
	}
	return {
		ok: true,
		message: extractMessage(res, null),
		data: res.data ?? res,
	};
}

export function requireWebsiteId(websiteId) {
	if (websiteId == null || websiteId === "") {
		return { ok: false, message: "websiteId is required", data: null };
	}
	return null;
}

export function toJobId(jobId) {
	return Number.isFinite(Number(jobId)) ? Number(jobId) : jobId;
}

function sleep(ms) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Normalize deploy job payload from sbend/grader. */
export function normalizeDeployJob(raw) {
	if (!raw || typeof raw !== "object") return null;
	const status = String(raw.status || "").toLowerCase();
	return {
		id: raw.id ?? raw.job_id ?? raw.jobId ?? null,
		status,
		currentStep: raw.current_step || raw.currentStep || null,
		sourceUrl: raw.source_url || raw.sourceUrl || null,
		instructions: raw.instructions || null,
		tempPageUrl: raw.temp_page_url || raw.tempPageUrl || null,
		verificationPassed:
			raw.verification_passed ?? raw.verificationPassed ?? null,
		errorMessage: raw.error_message || raw.errorMessage || null,
		organizationId: raw.organization_id || raw.organizationId || null,
		websiteId: raw.website_id || raw.websiteId || null,
		writingJobId: raw.writing_job_id ?? raw.writingJobId ?? null,
		changeIndex: raw.change_index ?? raw.changeIndex ?? null,
		recommendationId: raw.recommendation_id ?? raw.recommendationId ?? null,
		startedAt: raw.started_at || raw.startedAt || null,
		completedAt: raw.completed_at || raw.completedAt || null,
		durationMs: raw.duration_ms ?? raw.durationMs ?? null,
		createdAt: raw.created_at || raw.createdAt || null,
		updatedAt: raw.updated_at || raw.updatedAt || null,
		taskId: raw.task_id || raw.taskId || null,
		message: raw.message || null,
		pollForStatus: raw.poll_for_status ?? raw.pollForStatus ?? true,
		isRevert: Boolean(raw.is_revert ?? raw.isRevert),
	};
}

export function normalizeLaterJob(raw) {
	if (!raw || typeof raw !== "object") return null;
	return {
		jobId: raw.job_id ?? raw.jobId ?? null,
		changeIndex: raw.change_index ?? raw.changeIndex ?? null,
		changeIndices: raw.change_indices ?? raw.changeIndices ?? null,
		writingJobId: raw.writing_job_id ?? raw.writingJobId ?? null,
		overwrittenAt: raw.overwritten_at || raw.overwrittenAt || null,
		sourceUrl: raw.source_url || raw.sourceUrl || null,
	};
}

export function normalizeVerification(raw) {
	if (!raw || typeof raw !== "object") return null;
	const diff = raw.diff_summary || raw.diffSummary || {};
	return {
		passed: Boolean(raw.passed),
		screenshotOriginal:
			raw.screenshot_original || raw.screenshotOriginal || null,
		screenshotModified:
			raw.screenshot_modified || raw.screenshotModified || null,
		screenshotDiff: raw.screenshot_diff || raw.screenshotDiff || null,
		diffSummary: {
			similarity: diff.similarity ?? null,
			differentPixels: diff.different_pixels ?? diff.differentPixels ?? null,
			totalPixels: diff.total_pixels ?? diff.totalPixels ?? null,
			percentageDifferent:
				diff.percentage_different ?? diff.percentageDifferent ?? null,
		},
		consoleErrors: raw.console_errors || raw.consoleErrors || {
			original: [],
			temp: [],
		},
		noindexVerified: raw.noindex_verified ?? raw.noindexVerified ?? null,
	};
}

export function isDeployJobFailed(status, errorMessage) {
	const s = String(status || "").toLowerCase();
	if (
		s === "failed" ||
		s === "failed_patch" ||
		s === "error" ||
		s === "cancelled" ||
		s.startsWith("failed_")
	) {
		return true;
	}
	return errorMessage != null && errorMessage !== "";
}

export function isDeployAwaitingReview(status) {
	return String(status || "").toLowerCase() === "awaiting_review";
}

export function isDeployTerminal(status) {
	const s = String(status || "").toLowerCase();
	return s === "discarded" || s === "overwritten" || s === "reverted";
}

/**
 * Poll getDeployJob until status is in untilStatuses (or failed).
 */
export async function pollDeployJobWith(
	getDeployJob,
	websiteId,
	jobId,
	{
		untilStatuses = ["awaiting_review"],
		intervalMs = DEPLOY_JOB_POLL_MS,
		onTick,
	} = {}
) {
	if (websiteId == null || jobId == null || jobId === "") {
		return { ok: false, message: "Missing website or job id", job: null };
	}
	const targets = new Set(
		(untilStatuses || []).map((s) => String(s).toLowerCase())
	);

	for (;;) {
		const result = await getDeployJob(websiteId, jobId);
		if (!result.ok) {
			return { ok: false, message: result.message, job: null };
		}

		const job = result.job;
		if (typeof onTick === "function") onTick(job);

		if (isDeployJobFailed(job?.status, job?.errorMessage)) {
			return {
				ok: false,
				message: job?.errorMessage || "Deploy job failed",
				job,
			};
		}

		if (targets.has(String(job?.status || "").toLowerCase())) {
			return { ok: true, message: null, job };
		}

		if (job?.pollForStatus === false) {
			return { ok: true, message: null, job };
		}

		await sleep(intervalMs);
	}
}
